import { CdkContextMenuTrigger, CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  viewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MdwFade } from '@spaces-ui/animations/angular-css';
import { MdwButton } from '@spaces-ui/meadow/components/button';
import { MdwListItem } from '@spaces-ui/meadow/components/list-item';
import { MdwLoader } from '@spaces-ui/meadow/components/loader';
import { MdwScrollIsNearEdge } from '@spaces-ui/meadow/components/scrolling';
import { MdwTooltipIfClipped } from '@spaces-ui/meadow/components/tooltip';
import { MdwIcon } from '@spaces-ui/meadow/icons';
import { Overflow, Search } from '@spaces-ui/meadow/icons/svgs';
import { EventUtils } from '@spaces-ui/utils';

import { TableConfig, TableSort } from '../../models';
import {
  CellEditEvent,
  CellNavigationEvent,
  MdwEditableCell,
} from '../editable-cell/editable-cell';
import { MdwTableSortHeader } from '../sort-header/sort-header';

@Component({
  selector: 'mdw-table',
  templateUrl: './table.html',
  styleUrls: ['./table.scss'],
  imports: [
    CommonModule,
    ScrollingModule,
    MdwListItem,
    MdwTableSortHeader,
    MdwButton,
    CdkMenuItem,
    MdwIcon,
    MdwScrollIsNearEdge,
    MatTooltipModule,
    MdwTooltipIfClipped,
    MdwLoader,
    CdkMenu,
    CdkMenuTrigger,
    CdkContextMenuTrigger,
    MdwEditableCell,
    MdwFade,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.has-selection]': 'anySelected()',
  },
})
export class MdwTable<T> implements AfterViewInit {
  public readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  public readonly scrollStyle = input<'immediate' | 'virtual'>('virtual');
  public readonly rowHeight = input<number | 'unset'>(56); // Don't use unset for virtual scrolling
  public readonly rowPadding = input<number>(); // only applies to non virtual scrolling
  public readonly tableConfig = input.required<TableConfig<T>>();
  public readonly scrollRightBuffer = input<number>(0);
  public readonly scrollIsNearEdgeType = input<'top' | 'bottom'>('bottom');
  public readonly loading = input<boolean>(false);
  public readonly loadingReplacesContent = input<boolean>(false);
  // Surface token the selection toolbar paints over the column headers; pick the
  // one that matches the surface the table sits on so the overlay blends in.
  public readonly selectionToolbarSurface = input<'Flat' | 'Background'>('Flat');

  public readonly scrollableRef = viewChild('scrollable', { read: HTMLElement });
  public readonly virtualScrollContainer = viewChild(CdkVirtualScrollViewport);
  public readonly editableCells = viewChildren(MdwEditableCell);

  public readonly sort = output<TableSort<T>>();
  public readonly handleAction = output<{ action: string; item: T }>();
  public readonly cellEdited = output<CellEditEvent<T>>();
  public readonly nearBottomEdge = output<void>();
  public readonly nearTopEdge = output<void>();
  public readonly selectionChange = output<T[]>();
  public readonly handleSelectionAction = output<{ action: string; selectedItems: T[] }>();

  public readonly icons = { Overflow, Search };

  public readonly viewRendered = signal<boolean>(false);
  public readonly hasScroll = signal<boolean>(false);

  // Keyed by row key (see keyOf) and holding the full item object, so a selection
  // survives the parent filtering it out of `tableConfig.items` — the table never
  // sees the unfiltered list, so it must retain the objects it had at selection time.
  private readonly selection = signal<Map<string, T>>(new Map());
  private hasWarnedMissingTrackBy = false;

  public readonly selectedItems = computed<T[]>(() => Array.from(this.selection().values()));
  public readonly selectedCount = computed<number>(() => this.selection().size);
  public readonly anySelected = computed<boolean>(() => this.selectedCount() > 0);
  // The header checkbox reflects the currently-visible rows, not the full selection:
  // checked when every visible row is selected, indeterminate when only some are.
  public readonly allSelected = computed<boolean>(() => {
    const items = this.tableConfig().items;
    if (items.length === 0) {
      return false;
    }

    const selection = this.selection();
    return items.every(item => selection.has(this.keyOf(item)));
  });
  public readonly someSelected = computed<boolean>(() => {
    const items = this.tableConfig().items;
    const selection = this.selection();
    const visibleSelected = items.filter(item => selection.has(this.keyOf(item))).length;

    return visibleSelected > 0 && visibleSelected < items.length;
  });
  // Mirrors selectedCount but only ever holds the last non-zero value, so the toolbar
  // label doesn't flash "0 items selected" while it fades out on deselection.
  public readonly displaySelectionCount = signal<number>(0);

  public readonly selectionLabel = computed<string>(() => {
    const count = this.displaySelectionCount();
    const itemLabel = this.tableConfig().itemLabel;
    const noun = count === 1 ? (itemLabel?.singular ?? 'item') : (itemLabel?.plural ?? 'items');

    return `${count} ${noun} selected`;
  });

  public readonly rowActions = computed(
    () =>
      this.tableConfig().columns.find(col => col.actions && col.actions.length > 0)?.actions ?? [],
  );

  // The item whose actions menu is open. The menu template reads this signal instead of
  // trigger context data because CDK caches the menu's TemplatePortal (and its context)
  // across opens, which would otherwise serve stale row data after an item updates.
  public readonly actionsMenuItem = signal<T | undefined>(undefined);

  public constructor() {
    effect(() => {
      this.tableConfig();
      this.viewRendered();
      const scrolStyle = this.scrollStyle();
      const virtualScroll = this.virtualScrollContainer();

      setTimeout(() => {
        const scrollable = this.elementRef?.nativeElement?.querySelector('.scrollable');
        if (!scrollable) {
          this.hasScroll.set(false);
          return;
        }

        this.hasScroll.set(scrollable.scrollHeight > scrollable.clientHeight);

        if (!virtualScroll || scrolStyle === 'immediate') {
          return;
        }

        virtualScroll.checkViewportSize();
      }, 200);
    });

    effect(() => {
      const count = this.selectedCount();
      if (count > 0) {
        untracked(() => this.displaySelectionCount.set(count));
      }
    });

    // The facades recreate item objects on every data change (e.g. presence
    // updates), so a selection captured earlier can hold a stale object. When the
    // visible items change, refresh the stored object for any selected, currently-
    // visible row. Selections filtered out of view keep their last-known object —
    // the best we can do without the full, unfiltered list.
    effect(() => {
      const items = this.tableConfig().items;
      untracked(() => {
        const selection = this.selection();
        if (selection.size === 0) {
          return;
        }

        let changed = false;
        const next = new Map(selection);
        for (const item of items) {
          const key = this.keyOf(item);
          if (next.has(key) && next.get(key) !== item) {
            next.set(key, item);
            changed = true;
          }
        }

        // Membership is unchanged, so don't emit selectionChange — only the
        // stored object references are refreshed.
        if (changed) {
          this.selection.set(next);
        }
      });
    });
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.viewRendered.set(true);
    });
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public trackBy(index: number, item: T) {
    const config = this.tableConfig();

    if (config.trackBy) {
      return config.trackBy(index, item);
    }

    return this.trackByIndex(index);
  }

  public trackColumn(column: TableConfig<T>['columns'][0]) {
    return column.actions && column.actions.length > 0 ? 'actions' : column.name;
  }

  private keyOf(item: T): string {
    const config = this.tableConfig();

    if (config.trackBy) {
      return String(config.trackBy(0, item));
    }

    if (config.selectable && !this.hasWarnedMissingTrackBy) {
      this.hasWarnedMissingTrackBy = true;
      console.warn(
        'mdw-table: `selectable` requires a `trackBy` in TableConfig to uniquely identify rows. ' +
          'Selection may behave unexpectedly without it.',
      );
    }

    return String(this.tableConfig().items.indexOf(item));
  }

  public isSelected(item: T): boolean {
    return this.selection().has(this.keyOf(item));
  }

  public toggleItem(item: T): void {
    const next = new Map(this.selection());
    const key = this.keyOf(item);

    if (next.has(key)) {
      next.delete(key);
    } else {
      next.set(key, item);
    }

    this.selection.set(next);
    this.selectionChange.emit(this.selectedItems());
  }

  public toggleAll(): void {
    const items = this.tableConfig().items;
    const next = new Map(this.selection());

    if (this.allSelected()) {
      // Deselect the currently-visible rows, leaving any off-screen selections intact.
      for (const item of items) {
        next.delete(this.keyOf(item));
      }
    } else {
      // Add the currently-visible rows to the selection.
      for (const item of items) {
        next.set(this.keyOf(item), item);
      }
    }

    this.selection.set(next);
    this.selectionChange.emit(this.selectedItems());
  }

  public clearSelection(): void {
    if (this.selection().size === 0) {
      return;
    }

    this.selection.set(new Map());
    this.selectionChange.emit(this.selectedItems());
  }

  public emitSelectionAction(name: string): void {
    this.handleSelectionAction.emit({ action: name, selectedItems: this.selectedItems() });
  }

  public getTooltip(cell: HTMLElement, textValue: string) {
    if (cell.scrollWidth > cell.clientWidth) {
      return textValue;
    }

    return '';
  }

  public preventBubble(event: Event) {
    EventUtils.preventBubble(event);
  }

  public handleCellNavigation(event: CellNavigationEvent<T>): void {
    const config = this.tableConfig();
    const items = config.items;

    const currentItemIndex = items.findIndex(
      item => config.trackBy?.(0, item) === config.trackBy?.(0, event.item),
    );

    const editableColumns = config.columns
      .map((col, index) => ({ col, index }))
      .filter(({ col }) => col.editable && col.name);

    const currentColIndex = editableColumns.findIndex(({ col }) => col.name === event.field);

    if (currentItemIndex === -1 || currentColIndex === -1) {
      return;
    }

    let nextItemIndex = currentItemIndex;
    let nextColIndex = currentColIndex;
    const step = event.reverse ? -1 : 1;

    // Keep searching until we find an editable cell or run out of items
    while (nextItemIndex >= 0 && nextItemIndex < items.length) {
      if (event.direction === 'horizontal') {
        nextColIndex = nextColIndex + step;
        if (nextColIndex >= editableColumns.length) {
          nextColIndex = 0;
          nextItemIndex = nextItemIndex + 1;
        } else if (nextColIndex < 0) {
          nextColIndex = editableColumns.length - 1;
          nextItemIndex = nextItemIndex - 1;
        }
      } else {
        nextItemIndex = nextItemIndex + step;
      }

      if (nextItemIndex < 0 || nextItemIndex >= items.length) {
        return;
      }

      const nextItem = items[nextItemIndex];
      const nextColumn = editableColumns[nextColIndex]?.col;
      const nextField = nextColumn?.name;

      if (!nextField) {
        return;
      }

      // Check if this cell is editable for this item
      const canEditCell = nextColumn.editable?.canEdit
        ? nextColumn.editable.canEdit(nextItem)
        : true;

      if (canEditCell) {
        // For virtual scroll, we need to scroll to the row first, then find and edit the cell
        this.scrollToIndexAndEdit(nextItemIndex, nextItem, nextField);
        return;
      }
    }
  }

  private scrollToIndexAndEdit(index: number, item: T, field: keyof T): void {
    const virtualScroll = this.virtualScrollContainer();
    const config = this.tableConfig();

    if (virtualScroll && this.scrollStyle() === 'virtual') {
      const viewportSize = virtualScroll.getViewportSize();
      const rowHeight = this.rowHeight();
      const currentOffset = virtualScroll.measureScrollOffset();

      // Calculate if target row is outside visible viewport
      const targetOffset = typeof rowHeight === 'number' ? index * rowHeight : 0;
      const isAboveViewport = targetOffset < currentOffset;
      const isbelowViewport =
        typeof rowHeight === 'number' && targetOffset + rowHeight > currentOffset + viewportSize;

      if (isAboveViewport || isbelowViewport) {
        // Target row is outside viewport - scroll to it
        virtualScroll.scrollToIndex(index, 'smooth');

        // Wait for scroll and render, then find and edit the cell
        setTimeout(() => {
          this.findAndEditCell(item, field, config);
        }, 100);
      } else {
        // Target row should be visible - try to find and edit immediately
        setTimeout(() => {
          this.findAndEditCell(item, field, config);
        }, 0);
      }
    } else {
      // Non-virtual scroll - cell should already be in DOM
      setTimeout(() => {
        this.findAndEditCell(item, field, config);
      }, 0);
    }
  }

  private findAndEditCell(item: T, field: keyof T, config: TableConfig<T>): void {
    const cells = this.editableCells();
    const nextCell = cells.find(
      cell =>
        config.trackBy?.(0, cell.item()) === config.trackBy?.(0, item) &&
        cell.columnName() === field,
    );

    if (nextCell?.editingEnabled()) {
      nextCell.enterEditMode();
    }
  }
}
